import AiAgentsAPI from '../../api/aiAgents';

const state = {
  agents: [],
  currentAgent: null,
  conversations: [],
  currentConversation: null,
  messages: [],
  uiFlags: {
    isFetchingAgents: false,
    isFetchingConversations: false,
    isFetchingMessages: false,
    isSendingMessage: false,
    isStreaming: false,
    isCreatingAgent: false,
  },
};

const getters = {
  getAgents: $state => $state.agents,
  getCurrentAgent: $state => $state.currentAgent,
  getConversations: $state => $state.conversations,
  getCurrentConversation: $state => $state.currentConversation,
  getMessages: $state => $state.messages,
  getUIFlags: $state => $state.uiFlags,
};

const mutations = {
  SET_AGENTS($state, agents) {
    $state.agents = agents;
  },
  SET_CURRENT_AGENT($state, agent) {
    $state.currentAgent = agent;
  },
  ADD_AGENT($state, agent) {
    $state.agents.push(agent);
  },
  UPDATE_AGENT($state, updatedAgent) {
    const idx = $state.agents.findIndex(a => a.id === updatedAgent.id);
    if (idx !== -1) $state.agents.splice(idx, 1, updatedAgent);
    if ($state.currentAgent?.id === updatedAgent.id) {
      $state.currentAgent = updatedAgent;
    }
  },
  REMOVE_AGENT($state, agentId) {
    $state.agents = $state.agents.filter(a => a.id !== agentId);
    if ($state.currentAgent?.id === agentId) $state.currentAgent = null;
  },
  SET_CONVERSATIONS($state, conversations) {
    $state.conversations = conversations;
  },
  ADD_CONVERSATION($state, conversation) {
    $state.conversations.unshift(conversation);
  },
  SET_CURRENT_CONVERSATION($state, conversation) {
    $state.currentConversation = conversation;
  },
  REMOVE_CONVERSATION($state, conversationId) {
    const id = Number(conversationId);
    $state.conversations = $state.conversations.filter(c => c.id !== id);
    if ($state.currentConversation?.id === id) {
      $state.currentConversation = null;
      $state.messages = [];
    }
  },
  SET_MESSAGES($state, messages) {
    $state.messages = messages;
  },
  ADD_MESSAGE($state, message) {
    $state.messages.push(message);
  },
  // Append a token to the last assistant message (streaming)
  APPEND_TO_LAST_MESSAGE($state, token) {
    const lastMsg = $state.messages[$state.messages.length - 1];
    if (lastMsg && lastMsg.role === 'assistant') {
      lastMsg.content += token;
    }
  },
  // Replace a temporary message by id
  REPLACE_MESSAGE($state, { tempId, message }) {
    const idx = $state.messages.findIndex(m => m.id === tempId);
    if (idx !== -1) {
      $state.messages.splice(idx, 1, message);
    }
  },
  // Update the last message with final data
  FINALIZE_LAST_MESSAGE($state, message) {
    const idx = $state.messages.length - 1;
    if (idx >= 0 && $state.messages[idx].role === 'assistant') {
      $state.messages.splice(idx, 1, message);
    }
  },
  SET_UI_FLAG($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
};

const actions = {
  async fetchAgents({ commit }) {
    commit('SET_UI_FLAG', { isFetchingAgents: true });
    try {
      const { data } = await AiAgentsAPI.getAgents();
      commit('SET_AGENTS', data);
    } catch (error) {
      // handle error silently
    } finally {
      commit('SET_UI_FLAG', { isFetchingAgents: false });
    }
  },

  async fetchAgent({ commit }, agentId) {
    try {
      const { data } = await AiAgentsAPI.getAgent(agentId);
      commit('SET_CURRENT_AGENT', data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  async createAgent({ commit }, agentData) {
    commit('SET_UI_FLAG', { isCreatingAgent: true });
    try {
      const { data } = await AiAgentsAPI.createAgent(agentData);
      commit('ADD_AGENT', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isCreatingAgent: false });
    }
  },

  async updateAgent({ commit }, { agentId, agentData }) {
    try {
      const { data } = await AiAgentsAPI.updateAgent(agentId, agentData);
      commit('UPDATE_AGENT', data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  async deleteAgent({ commit }, agentId) {
    try {
      await AiAgentsAPI.deleteAgent(agentId);
      commit('REMOVE_AGENT', agentId);
    } catch (error) {
      throw error;
    }
  },

  async fetchConversations({ commit }, agentId) {
    commit('SET_UI_FLAG', { isFetchingConversations: true });
    try {
      const { data } = await AiAgentsAPI.getConversations(agentId);
      commit('SET_CONVERSATIONS', data);
    } catch (error) {
      // handle silently
    } finally {
      commit('SET_UI_FLAG', { isFetchingConversations: false });
    }
  },

  async createConversation({ commit }, agentId) {
    try {
      const { data } = await AiAgentsAPI.createConversation(agentId);
      commit('ADD_CONVERSATION', data);
      commit('SET_CURRENT_CONVERSATION', data);
      commit('SET_MESSAGES', []);
      return data;
    } catch (error) {
      throw error;
    }
  },

  async fetchConversation({ commit }, { agentId, conversationId }) {
    commit('SET_UI_FLAG', { isFetchingMessages: true });
    try {
      const { data } = await AiAgentsAPI.getConversation(
        agentId,
        conversationId
      );
      commit('SET_CURRENT_CONVERSATION', data.conversation);
      commit('SET_MESSAGES', data.messages);
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetchingMessages: false });
    }
  },

  /**
   * Send message with SSE streaming.
   * Reads the stream and commits tokens in real-time.
   */
  async sendMessage({ commit }, { agentId, conversationId, content }) {
    commit('SET_UI_FLAG', { isSendingMessage: true });

    try {
      const response = await AiAgentsAPI.sendMessageStream(
        agentId,
        conversationId,
        content
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamingStarted = false;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const dataStr = line.slice(6);
            try {
              const data = JSON.parse(dataStr);

              if (eventType === 'user_message') {
                commit('ADD_MESSAGE', data);
              } else if (eventType === 'chunk') {
                if (!streamingStarted) {
                  // Add placeholder assistant message
                  commit('ADD_MESSAGE', {
                    id: `streaming-${Date.now()}`,
                    role: 'assistant',
                    content: data.content,
                    status: 'pending',
                    created_at: new Date().toISOString(),
                  });
                  streamingStarted = true;
                  commit('SET_UI_FLAG', { isStreaming: true });
                } else {
                  commit('APPEND_TO_LAST_MESSAGE', data.content);
                }
              } else if (eventType === 'done') {
                commit('FINALIZE_LAST_MESSAGE', data);
                commit('SET_UI_FLAG', { isStreaming: false });
              }
            } catch {
              // skip malformed JSON
            }
            eventType = '';
          }
        }
      }
    } catch (error) {
      commit('SET_UI_FLAG', { isStreaming: false });
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isSendingMessage: false });
    }
  },

  clearCurrentChat({ commit }) {
    commit('SET_CURRENT_CONVERSATION', null);
    commit('SET_MESSAGES', []);
  },

  async deleteConversation({ commit }, { agentId, conversationId }) {
    await AiAgentsAPI.deleteConversation(agentId, conversationId);
    commit('REMOVE_CONVERSATION', conversationId);
  },

  async addParticipant({ commit }, { agentId, conversationId, userId }) {
    const { data } = await AiAgentsAPI.addParticipant(agentId, conversationId, userId);
    commit('SET_CURRENT_CONVERSATION', data);
    return data;
  },

  async removeParticipant({ commit }, { agentId, conversationId, userId }) {
    const { data } = await AiAgentsAPI.removeParticipant(agentId, conversationId, userId);
    commit('SET_CURRENT_CONVERSATION', data);
    return data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
